import * as React from 'react';

import { faPencil, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faChevronDown, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    createMembershipGroupCardTagFormSchema,
    type CreateMembershipGroupCardTagFormSchemaValues,
    useCreateMembershipGroupCardTag,
} from 'src/features/memberships/api/useCreateMembershipGroupCardTag';
import { useDeleteMembershipGroupCardTag } from 'src/features/memberships/api/useDeleteMembershipGroupCardTag';
import { useUpdateMembershipGroupCardTag } from 'src/features/memberships/api/useUpdateMembershipGroupCardTag';
import { badgeThemes } from 'src/features/memberships/utils/constants';
import { badgeColorMessages, membershipMessages } from 'src/features/memberships/utils/messages';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandLoading,
} from 'src/registry/ui/Command';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { Form, FormControl, FormField, FormItem, FormLabel } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { RadioGroup, RadioGroupItem } from 'src/registry/ui/RadioGroup';
import { Separator } from 'src/registry/ui/Separator';
import { generalMessages } from 'src/utils/messages';
import { cn } from 'src/utils/shadcn';

import type { MembershipGroupCardTag } from 'src/features/memberships/types';

interface MembershipGroupCardTagsComboboxProps {
    value: string[];
    onChange: (value: string[]) => void;
    membershipGroupCardTags?: MembershipGroupCardTag[];
    isLoading?: boolean;
    parkingId: string;
    membershipGroupId: number;
    refetch: () => void;
}
const MembershipGroupCardTagsCombobox = ({
    value,
    onChange,
    membershipGroupCardTags,
    isLoading,
    parkingId,
    membershipGroupId,
    refetch,
}: MembershipGroupCardTagsComboboxProps) => {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [tab, setTab] = React.useState<'list' | 'create' | 'edit'>('list');
    const [editTag, setEditTag] = React.useState<MembershipGroupCardTag | null>(null);

    const { formatMessage } = useIntl();

    const selectedMembershipGroupCardTags = React.useMemo(() => {
        if (!membershipGroupCardTags) return [];
        return membershipGroupCardTags.filter((membershipGroupCardTag) => value.includes(membershipGroupCardTag.id));
    }, [value, membershipGroupCardTags]);

    const showEditButton = React.useMemo(() => {
        return membershipGroupCardTags && membershipGroupCardTags?.length > 0;
    }, [membershipGroupCardTags]);

    const { mutate: createMembershipGroupCardTag, isPending: isPendingCreateMembershipGroupCardTag } =
        useCreateMembershipGroupCardTag({
            onSuccess: (tag) => {
                refetch();
                onChange([...value, tag.id]);
                setSearch('');
                setTab('list');
                toast({
                    title: formatMessage(membershipMessages.createdTag),
                });
            },
            onError: () => {
                toast({
                    variant: 'destructive',
                    title: formatMessage(membershipMessages.failedToCreateCardTag),
                    description: formatMessage(membershipMessages.failedToCreateCardTagDescription),
                });
            },
        });

    const { mutate: updateMembershipGroupCardTag, isPending: isPendingUpdateMembershipGroupCardTag } =
        useUpdateMembershipGroupCardTag({
            onSuccess: () => {
                refetch();
                setEditTag(null);
                toast({
                    title: formatMessage(membershipMessages.updatedTag),
                });
            },
            onError: () => {
                toast({
                    variant: 'destructive',
                    title: formatMessage(membershipMessages.failedToUpdateCardTag),
                    description: formatMessage(membershipMessages.failedToUpdateCardTagDescription),
                });
            },
        });

    const { mutate: deleteMembershipGroupCardTag } = useDeleteMembershipGroupCardTag({
        onSuccess: () => {
            refetch();
            setEditTag(null);
            toast({
                title: formatMessage(membershipMessages.deletedTag),
            });
        },
    });

    const form = useForm<CreateMembershipGroupCardTagFormSchemaValues>({
        resolver: zodResolver(createMembershipGroupCardTagFormSchema),
        defaultValues: {
            text: search,
            theme: '',
        },
    });

    const handleSubmit = (values: CreateMembershipGroupCardTagFormSchemaValues) => {
        const submitData = {
            membershipGroupId,
            parkingId,
            ...values,
        };

        if (editTag?.id) {
            updateMembershipGroupCardTag({ membershipGroupCardTagId: editTag.id, ...submitData });
        } else {
            createMembershipGroupCardTag(submitData);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button className="h-auto flex-wrap" variant="dropdown" role="combobox" aria-expanded={open}>
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className="text-primary-700 my-1.5 mr-1 size-3 w-4 shrink-0"
                    />
                    {selectedMembershipGroupCardTags.length > 0 ? (
                        selectedMembershipGroupCardTags.map((membershipGroupTag) => {
                            return (
                                <Badge key={`${membershipGroupTag.id}-badge`} theme={membershipGroupTag.theme}>
                                    {membershipGroupTag.text}
                                </Badge>
                            );
                        })
                    ) : (
                        <p className="text-gray-400">
                            <FormattedMessage {...membershipMessages.tagsSearchPlaceholder} />
                        </p>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                style={{ width: 'var(--radix-popover-trigger-width)' }}
                align="start"
                className={cn(
                    'p-0',
                    showEditButton || search.length ? 'pb-10' : 'pb-0',
                    showEditButton && search.length && 'pb-18',
                    tab != 'list' && 'pb-0',
                    membershipGroupCardTags?.length === 0 && tab === 'list' && 'pb-10',
                )}
            >
                {tab !== 'create' && !editTag && (
                    <Command>
                        <CommandInput
                            value={search}
                            onValueChange={(value) => {
                                setSearch(value);
                            }}
                            placeholder={
                                tab === 'edit'
                                    ? formatMessage(membershipMessages.tagsEditPlaceholder)
                                    : formatMessage(membershipMessages.tagsSearchPlaceholder)
                            }
                            tab={tab}
                            maxLength={255}
                        />
                        <CommandList>
                            {isLoading && <CommandLoading>Loading...</CommandLoading>}

                            <CommandEmpty className="flex flex-col items-center justify-center p-1 px-4 py-8 text-center text-balance">
                                <p className="text-sm font-semibold">
                                    <FormattedMessage {...membershipMessages.noTags} />
                                </p>
                                <p className="text-sm text-gray-500">
                                    <FormattedMessage {...membershipMessages.noTagsDescription} />
                                </p>
                            </CommandEmpty>
                            <CommandGroup className={cn(!membershipGroupCardTags?.length && 'hidden')}>
                                {membershipGroupCardTags?.map((membershipGroupTag) => (
                                    <CommandItem
                                        key={membershipGroupTag.id}
                                        value={membershipGroupTag.id}
                                        keywords={[membershipGroupTag.text]}
                                        onSelect={() => {
                                            if (tab === 'edit') {
                                                setEditTag(membershipGroupTag);
                                                form.setValue('text', membershipGroupTag.text);
                                                form.setValue('theme', membershipGroupTag.theme);
                                            } else {
                                                if (value.includes(membershipGroupTag.id)) {
                                                    onChange(value.filter((value) => value !== membershipGroupTag.id));
                                                } else {
                                                    onChange([...value, membershipGroupTag.id]);
                                                }
                                            }
                                        }}
                                    >
                                        {tab !== 'edit' && (
                                            <FontAwesomeIcon
                                                icon={faCheck}
                                                className={cn(
                                                    'text-primary-700 mr-1 size-3 w-4 shrink-0',
                                                    value.includes(membershipGroupTag.id) ? 'opacity-100' : 'opacity-0',
                                                )}
                                            />
                                        )}
                                        <Badge theme={membershipGroupTag.theme}>{membershipGroupTag.text}</Badge>
                                    </CommandItem>
                                ))}
                            </CommandGroup>

                            {tab !== 'edit' && (
                                <CommandGroup
                                    forceMount
                                    className="absolute bottom-0 left-0 z-50 w-full rounded-b border bg-white"
                                >
                                    {(search.length > 0 || membershipGroupCardTags?.length == 0) && (
                                        <CommandItem
                                            onSelect={() => {
                                                form.setValue('text', search);
                                                setTab('create');
                                            }}
                                            className="cursor-pointer"
                                            value="create-tag"
                                        >
                                            <FontAwesomeIcon
                                                icon={faPlus}
                                                className={cn('text-primary-700 mr-1 size-3 w-4 shrink-0')}
                                            />
                                            <FormattedMessage
                                                {...membershipMessages.createTag}
                                                values={{ tag: search }}
                                            />
                                        </CommandItem>
                                    )}
                                    {membershipGroupCardTags && membershipGroupCardTags?.length > 0 && (
                                        <CommandItem
                                            onSelect={() => {
                                                setTab('edit');
                                                setSearch('');
                                            }}
                                            value="edit-tags"
                                            className="focus:bg-red-400"
                                        >
                                            <FontAwesomeIcon
                                                icon={faPencil}
                                                className={cn('text-primary-700 mr-1 size-3 w-4 shrink-0')}
                                            />
                                            <FormattedMessage
                                                {...membershipMessages.editTags}
                                                values={{ tag: search }}
                                            />
                                        </CommandItem>
                                    )}
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                )}

                {tab === 'edit' && !editTag && (
                    <div className="mt-auto grid gap-6 border-t p-6">
                        <Button onClick={() => setTab('list')} type="button" variant="outline">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </div>
                )}

                {(tab === 'create' || editTag) && (
                    <Form form={form} onSubmit={form.handleSubmit(handleSubmit)} className="gap-0 space-y-0 sm:gap-0">
                        <FormField
                            control={form.control}
                            name="text"
                            render={({ field }) => (
                                <FormItem variant="dialog" className="relative">
                                    <FontAwesomeIcon
                                        icon={faPlus}
                                        className="text-primary-700 pointer-events-none absolute top-4 left-4 size-3 w-4"
                                    />
                                    <FormControl>
                                        <Input
                                            type="string"
                                            placeholder={formatMessage(membershipMessages.tagName)}
                                            {...field}
                                            className="h-11 rounded-none border-none pl-11 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <Separator />
                        <FormField
                            control={form.control}
                            name="theme"
                            render={({ field }) => (
                                <FormControl>
                                    <RadioGroup
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                        className="relative max-h-64 gap-0 overflow-y-auto p-1"
                                    >
                                        {badgeThemes.map((theme) => (
                                            <FormItem
                                                key={theme}
                                                className="flex items-center gap-2 space-y-0 hover:bg-gray-100"
                                            >
                                                <FormControl>
                                                    <RadioGroupItem
                                                        onClick={() => field.onChange(theme)}
                                                        className="absolute left-4"
                                                        value={theme}
                                                    />
                                                </FormControl>
                                                <FormLabel className="h-full flex-1 py-1.5 pr-3 pl-10">
                                                    <Badge theme={theme}>
                                                        {form.watch('text').length > 0
                                                            ? form.watch('text')
                                                            : formatMessage(
                                                                  badgeColorMessages[
                                                                      theme as keyof typeof badgeColorMessages
                                                                  ],
                                                              )}
                                                    </Badge>
                                                </FormLabel>
                                            </FormItem>
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            )}
                        />
                        <div className="mt-auto grid grid-cols-2 gap-6 border-t p-6">
                            <Button
                                onClick={() => {
                                    if (tab === 'create') {
                                        setTab('list');
                                    } else {
                                        setEditTag(null);
                                    }
                                }}
                                type="button"
                                variant="outline"
                            >
                                <FormattedMessage {...generalMessages.cancel} />
                            </Button>
                            {editTag && (
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <Button type="button" variant="destructiveLight">
                                            <FormattedMessage {...membershipMessages.deleteTag} />
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader className="flex-row gap-4">
                                            <SquareIcon icon={faTrashAlt} />
                                            <DialogTitle>{formatMessage(membershipMessages.deleteTag)}</DialogTitle>
                                        </DialogHeader>

                                        <DialogSeparator />

                                        <DialogBody>
                                            <p className="text-gray-500">
                                                {formatMessage(membershipMessages.confirmDeleteTag, {
                                                    name: editTag?.text,
                                                })}
                                            </p>
                                        </DialogBody>

                                        <DialogSeparator />

                                        <DialogFooter>
                                            <DialogClose asChild>
                                                <Button variant="outline" type="button" className="flex-1">
                                                    {formatMessage(generalMessages.cancel)}
                                                </Button>
                                            </DialogClose>

                                            <Button
                                                variant="destructiveLight"
                                                onClick={() => {
                                                    if (membershipGroupId && parkingId && editTag) {
                                                        void deleteMembershipGroupCardTag({
                                                            membershipGroupCardTagId: editTag.id,
                                                            membershipGroupId,
                                                            parkingId,
                                                        });
                                                    }
                                                }}
                                                className="flex-1"
                                            >
                                                {formatMessage(membershipMessages.deleteTag)}
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            )}

                            <Button
                                disabled={
                                    isPendingUpdateMembershipGroupCardTag ||
                                    isPendingCreateMembershipGroupCardTag ||
                                    !form.formState.isValid
                                }
                                onClick={form.handleSubmit(handleSubmit)}
                                type="submit"
                                className={cn(editTag && 'col-span-2')}
                            >
                                <FormattedMessage {...membershipMessages.saveTag} />
                            </Button>
                        </div>
                    </Form>
                )}
            </PopoverContent>
        </Popover>
    );
};

export { MembershipGroupCardTagsCombobox };
